import React from 'react';
import Icon from 'components/AppIcon';

const SessionTable = ({
  sessions,
  selectedSessions,
  sortConfig,
  onSessionSelect,
  onSelectAll,
  onSort,
  onSessionDetails,
  onTerminateSession
}) => {
  const formatTime = (date) => {
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'только что';
    if (diffMins < 60) return `${diffMins} мин назад`;
    if (diffHours < 24) return `${diffHours} ч назад`;
    return `${diffDays} дн назад`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: {
        label: 'Активна',
        className: 'bg-success-100 text-success-700 border-success-200',
        icon: 'CheckCircle'
      },
      idle: {
        label: 'Неактивна',
        className: 'bg-warning-100 text-warning-700 border-warning-200',
        icon: 'Clock'
      },
      expired: {
        label: 'Истекла',
        className: 'bg-secondary-100 text-secondary-700 border-secondary-200',
        icon: 'XCircle'
      },
      suspicious: {
        label: 'Подозрительная',
        className: 'bg-error-100 text-error-700 border-error-200',
        icon: 'AlertTriangle'
      }
    };

    const config = statusConfig[status] || statusConfig.expired;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border ${config.className}`}>
        <Icon name={config.icon} size={12} />
        {config.label}
      </span>
    );
  };

  const getRiskBadge = (riskLevel) => {
    const riskConfig = {
      low: {
        label: 'Низкий',
        className: 'bg-success-50 text-success-600'
      },
      medium: {
        label: 'Средний',
        className: 'bg-warning-50 text-warning-600'
      },
      high: {
        label: 'Высокий',
        className: 'bg-error-50 text-error-600'
      }
    };

    const config = riskConfig[riskLevel] || riskConfig.low;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const getDeviceIcon = (deviceType) => {
    const icons = {
      Desktop: 'Monitor',
      Mobile: 'Smartphone',
      Tablet: 'Tablet'
    };
    return icons[deviceType] || 'Monitor';
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <Icon name="ArrowUpDown" size={14} className="text-secondary-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <Icon name="ArrowUp" size={14} className="text-primary" />
      : <Icon name="ArrowDown" size={14} className="text-primary" />;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-secondary-50 border-b border-secondary-200">
          <tr>
            <th className="w-12 px-4 py-3">
              <input
                type="checkbox"
                checked={selectedSessions.length === sessions.length && sessions.length > 0}
                onChange={onSelectAll}
                className="rounded border-secondary-300 text-primary focus:ring-primary/20"
              />
            </th>
            <th 
              className="px-4 py-3 text-left text-sm font-medium text-text-primary cursor-pointer hover:bg-secondary-100 transition-colors duration-150"
              onClick={() => onSort('userName')}
            >
              <div className="flex items-center gap-2">
                Пользователь
                {getSortIcon('userName')}
              </div>
            </th>
            <th 
              className="px-4 py-3 text-left text-sm font-medium text-text-primary cursor-pointer hover:bg-secondary-100 transition-colors duration-150"
              onClick={() => onSort('deviceType')}
            >
              <div className="flex items-center gap-2">
                Устройство
                {getSortIcon('deviceType')}
              </div>
            </th>
            <th 
              className="px-4 py-3 text-left text-sm font-medium text-text-primary cursor-pointer hover:bg-secondary-100 transition-colors duration-150"
              onClick={() => onSort('ipAddress')}
            >
              <div className="flex items-center gap-2">
                IP адрес
                {getSortIcon('ipAddress')}
              </div>
            </th>
            <th 
              className="px-4 py-3 text-left text-sm font-medium text-text-primary cursor-pointer hover:bg-secondary-100 transition-colors duration-150"
              onClick={() => onSort('location')}
            >
              <div className="flex items-center gap-2">
                Местоположение
                {getSortIcon('location')}
              </div>
            </th>
            <th 
              className="px-4 py-3 text-left text-sm font-medium text-text-primary cursor-pointer hover:bg-secondary-100 transition-colors duration-150"
              onClick={() => onSort('loginTime')}
            >
              <div className="flex items-center gap-2">
                Время входа
                {getSortIcon('loginTime')}
              </div>
            </th>
            <th 
              className="px-4 py-3 text-left text-sm font-medium text-text-primary cursor-pointer hover:bg-secondary-100 transition-colors duration-150"
              onClick={() => onSort('lastActivity')}
            >
              <div className="flex items-center gap-2">
                Последняя активность
                {getSortIcon('lastActivity')}
              </div>
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">
              Статус
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">
              Риск
            </th>
            <th className="px-4 py-3 text-center text-sm font-medium text-text-primary">
              Действия
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-secondary-100">
          {sessions.map((session) => (
            <tr 
              key={session.id}
              className={`hover:bg-secondary-25 transition-colors duration-150 ${
                selectedSessions.includes(session.id) ? 'bg-primary-50' : ''
              }`}
            >
              <td className="px-4 py-4">
                <input
                  type="checkbox"
                  checked={selectedSessions.includes(session.id)}
                  onChange={() => onSessionSelect(session.id)}
                  className="rounded border-secondary-300 text-primary focus:ring-primary/20"
                />
              </td>
              <td className="px-4 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-white">
                      {session.userName.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-text-primary">
                      {session.userName}
                      {session.isCurrent && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-accent-100 text-accent-700">
                          Текущая
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-text-secondary">{session.userEmail}</p>
                  </div>
                </div>
              </td>
              <td className="px-4 py-4">
                <div className="flex items-center gap-2">
                  <Icon name={getDeviceIcon(session.deviceType)} size={16} className="text-secondary-500" />
                  <div>
                    <p className="text-sm font-medium text-text-primary">{session.deviceType}</p>
                    <p className="text-xs text-text-secondary">{session.deviceName}</p>
                  </div>
                </div>
              </td>
              <td className="px-4 py-4">
                <span className="font-mono text-sm text-text-primary">{session.ipAddress}</span>
              </td>
              <td className="px-4 py-4">
                <div className="flex items-center gap-2">
                  <Icon name="MapPin" size={14} className="text-secondary-400" />
                  <span className="text-sm text-text-primary">{session.location}</span>
                </div>
              </td>
              <td className="px-4 py-4">
                <div>
                  <p className="text-sm text-text-primary">{formatTime(session.loginTime)}</p>
                  <p className="text-xs text-text-secondary">{session.sessionDuration}</p>
                </div>
              </td>
              <td className="px-4 py-4">
                <div>
                  <p className="text-sm text-text-primary">{formatTime(session.lastActivity)}</p>
                  <p className="text-xs text-text-secondary">{getTimeAgo(session.lastActivity)}</p>
                </div>
              </td>
              <td className="px-4 py-4">
                {getStatusBadge(session.status)}
              </td>
              <td className="px-4 py-4">
                {getRiskBadge(session.riskLevel)}
              </td>
              <td className="px-4 py-4">
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => onSessionDetails(session)}
                    className="p-1.5 rounded-md hover:bg-secondary-100 transition-colors duration-150"
                    title="Подробности сессии"
                  >
                    <Icon name="Eye" size={16} className="text-secondary-600" />
                  </button>
                  <button
                    className="p-1.5 rounded-md hover:bg-warning-100 transition-colors duration-150"
                    title="Отправить предупреждение"
                  >
                    <Icon name="AlertTriangle" size={16} className="text-warning-600" />
                  </button>
                  <button
                    onClick={() => onTerminateSession(session.id)}
                    className="p-1.5 rounded-md hover:bg-error-100 transition-colors duration-150"
                    title="Завершить сессию"
                  >
                    <Icon name="X" size={16} className="text-error-600" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {sessions.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Users" size={24} className="text-secondary-400" />
          </div>
          <h3 className="text-lg font-medium text-text-primary mb-2">
            Активные сессии не найдены
          </h3>
          <p className="text-text-secondary">
            В настоящее время нет активных пользовательских сессий
          </p>
        </div>
      )}
    </div>
  );
};

export default SessionTable;